import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../core/common/dataTable/resignation";
import { all_routes } from "../router/all_routes";
import CommonSelect from "../../core/common/resignationCommonselect";
import { DatePicker, Modal, Select, Tag } from "antd";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import { resignationApi } from "../../core/api/useresignationAPI";
import { API_BASE_PATH } from "../../environment";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { useAuthToken } from "../../core/data/redux/useAuthToken";
import { usePermission } from "../../core/api/useRolePermission";
import { useSelector } from "react-redux";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { fetchEmployeeImage } from "../../core/api/fetchEmployeeImage";

const { Option } = Select;

interface ResignationData {
  Image: string;
  Username: string;
  Resigning_Employee: string;
  Department: string;
  Reason: string;
  Notice_Date: string;
  Resignation_Date: string;
  Status?: "Pending" | "Approved" | "Rejected";
}

interface UpdateResignationDto {
  Status: "Approved" | "Rejected";
  reason?: string;
}

const Resignation: React.FC = () => {
  const empToken = useAuthToken();
  const empRole = useSelector((state: any) => state.auth.role);
  const authState = useSelector((state: any) => state.auth);
  const currentUserId = authState?.employeeId || "";
  const currentUserFullName = authState?.name || "";
  const currentUserDisplay =
    currentUserFullName && currentUserId
      ? `${currentUserFullName} (${currentUserId})`
      : "";

  const [resignationData, setResignationData] = useState<ResignationData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<ResignationData | null>(
    null
  );
  const [originalSelectedRecord, setOriginalSelectedRecord] =
    useState<ResignationData | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [deleteEmployeeId, setDeleteEmployeeId] = useState<string>("");
  const [employeeOptions, setEmployeeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [addEmployeeId, setAddEmployeeId] = useState<string>("");
  const [addNoticeDate, setAddNoticeDate] = useState<Dayjs | null>(null);
  const [addResignationDate, setAddResignationDate] = useState<Dayjs | null>(
    null
  );
  const [addReason, setAddReason] = useState("");
  const [noticeError, setNoticeError] = useState<string>("");
  const [isSelfResignation, setIsSelfResignation] = useState<boolean>(false);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({}); // Add state for image URLs

  // Status update states
  const [updatingStatuses, setUpdatingStatuses] = useState<
    Record<string, boolean>
  >({});
  const [rejectionModalVisible, setRejectionModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentRejectEmployee, setCurrentRejectEmployee] = useState<
    string | null
  >(null);

  const canCreate = usePermission("Resignation", "create");
  const canUpdate = usePermission("Resignation", "write");
  const canDelete = usePermission("Resignation", "delete");
  const canRead = usePermission("Resignation", "read");

  const getModalContainer = (): HTMLElement =>
    document.getElementById("modal-datepicker") || document.body;

  useEffect(() => {
    fetchDropdownOptions();
    fetchData();
  }, []);

  useEffect(() => {
    if (isSelfResignation) {
      setAddEmployeeId(currentUserId);
    }
  }, [isSelfResignation, currentUserId]);

  // Fetch images for all resignation records
  useEffect(() => {
    const fetchImages = async () => {
      const urlMap: Record<string, string> = {};
      for (const record of resignationData) {
        urlMap[record.Resigning_Employee] = await fetchEmployeeImage(
          record.Resigning_Employee,
          empToken
        );
      }
      setImageUrls(urlMap);
    };
    if (resignationData.length > 0) {
      fetchImages();
    }
  }, [resignationData, empToken]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await resignationApi.getAllResignations(empToken);
      const formattedData: ResignationData[] = response.map((item: any) => ({
        Image: item.Image || "user-01.jpg",
        Username: item.employee?.username || item.username || "N/A",
        Resigning_Employee:
          item.employee?.employee_id || item.employee_id || "N/A",
        Department: item.employee?.department_name || item.department || "N/A",
        Reason: item.reason || "-",
        Notice_Date: item.notice_date || "-",
        Resignation_Date: item.resignation_date || "-",
        Status: item.Status || "Pending",
      }));
      setResignationData(formattedData);
    } catch (error) {
      console.error("Error loading resignation data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      const response = await resignationApi.getEmployeeDepartmentDropdown(
        empToken
      );
      const options: { label: string; value: string }[] = response.map(
        (emp: any) => ({
          label: `${emp.username} (${emp.employee_id})`,
          value: emp.employee_id,
        })
      );

      // Explicit type for 'opt'
      if (
        !options.find(
          (opt: { label: string; value: string }) => opt.value === currentUserId
        ) &&
        currentUserId
      ) {
        options.unshift({
          label: `${currentUserFullName} (${currentUserId})`,
          value: currentUserId,
        });
      }

      setEmployeeOptions(options);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  // Handle status updates for Admin/Manager
  const handleStatusUpdate = async (
    employeeId: string,
    newStatus: "Approved" | "Rejected"
  ) => {
    if (newStatus === "Rejected") {
      setCurrentRejectEmployee(employeeId);
      setRejectionModalVisible(true);
      return;
    }

    setUpdatingStatuses((prev) => ({ ...prev, [employeeId]: true }));

    try {
      await resignationApi.updateResignation(empToken, employeeId, {
        Status: newStatus,
      } as UpdateResignationDto);

      // Update local state immediately for better UX
      setResignationData((prevData) =>
        prevData.map((item) =>
          item.Resigning_Employee === employeeId
            ? { ...item, Status: newStatus }
            : item
        )
      );
      // Refresh data to ensure consistency
      await fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      // Revert local state if API call failed
      setResignationData((prevData) =>
        prevData.map((item) =>
          item.Resigning_Employee === employeeId
            ? { ...item, Status: "Pending" }
            : item
        )
      );
    } finally {
      setUpdatingStatuses((prev) => ({ ...prev, [employeeId]: false }));
    }
  };

  // Handle rejection with reason
  const handleRejectSubmit = async () => {
    if (!currentRejectEmployee || !rejectionReason.trim()) {
      return toast.warning("Please provide a reason for rejection.");
    }

    setUpdatingStatuses((prev) => ({
      ...prev,
      [currentRejectEmployee!]: true,
    }));

    try {
      await resignationApi.updateResignation(empToken, currentRejectEmployee, {
        Status: "Rejected",
        reason: rejectionReason,
      } as UpdateResignationDto);

      // Update local state immediately
      setResignationData((prevData) =>
        prevData.map((item) =>
          item.Resigning_Employee === currentRejectEmployee
            ? { ...item, Status: "Rejected" }
            : item
        )
      );
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error("Error rejecting resignation:", error);

      // Revert state if failed
      setResignationData((prevData) =>
        prevData.map((item) =>
          item.Resigning_Employee === currentRejectEmployee
            ? { ...item, Status: "Pending" }
            : item
        )
      );
    } finally {
      setUpdatingStatuses((prev) => ({
        ...prev,
        [currentRejectEmployee!]: false,
      }));
      setRejectionModalVisible(false);
      setRejectionReason("");
      setCurrentRejectEmployee(null);
    }
  };

  const handleRejectCancel = () => {
    setRejectionModalVisible(false);
    setRejectionReason("");
    setCurrentRejectEmployee(null);
  };

  const handleRejectClick = (record: ResignationData) => {
    setCurrentRejectEmployee(record.Resigning_Employee);
    setRejectionModalVisible(true);
  };

  const handleUpdate = async () => {
    if (!canUpdate || !selectedRecord || noticeError) {
      toast.warning("Please fix all errors before updating.");
      return;
    }

    const payload = {
      reason: selectedRecord.Reason,
      notice_date: selectedRecord.Notice_Date,
      resignation_date: selectedRecord.Resignation_Date,
    };
    try {
      await resignationApi.updateResignation(
        empToken,
        selectedRecord.Resigning_Employee,
        payload
      );
      fetchData();

      // Close the Bootstrap modal programmatically
      const modalEl = document.getElementById("edit_resignation");
      if (modalEl) {
        const modalInstance = (window as any).bootstrap.Modal.getInstance(
          modalEl
        );
        modalInstance?.hide();
      }

      setSelectedRecord(null);
      setOriginalSelectedRecord(null);
      setNoticeError("");
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  const handleAddResignation = async () => {
    if (!canCreate) {
      return toast.warning("You don't have permission to create resignations.");
    }
    if (!addEmployeeId || !addNoticeDate || !addResignationDate || !addReason) {
      return toast.warning("Please fill all required fields.");
    }
    if (noticeError) {
      return toast.error("Please fix the date validation errors.");
    }

    const payload = {
      employee_id: addEmployeeId,
      reason: addReason,
      notice_date: addNoticeDate.format("YYYY-MM-DD"),
      resignation_date: addResignationDate.format("YYYY-MM-DD"),
    };

    try {
      await resignationApi.submitResignation(empToken, payload);
      clearAddForm();
      fetchData();

      // Close the Bootstrap modal programmatically
      const modalEl = document.getElementById("new_resignation");
      if (modalEl) {
        const modalInstance = (window as any).bootstrap.Modal.getInstance(
          modalEl
        );
        modalInstance?.hide();
      }
    } catch (error) {
      console.error("Error submitting resignation:", error);
    }
  };

  const handleDeleteClick = (employeeId: string) => {
    if (!canDelete) {
      return toast.warning("You don't have permission to delete resignations.");
    }
    setDeleteEmployeeId(employeeId);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || !deleteEmployeeId) return;

    try {
      await resignationApi.deleteResignation(empToken, deleteEmployeeId);
      fetchData();
      setDeleteEmployeeId("");
      setDeleteModalVisible(false);
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const clearAddForm = () => {
    setAddEmployeeId("");
    setAddNoticeDate(null);
    setAddResignationDate(null);
    setAddReason("");
    setNoticeError("");
    setIsSelfResignation(false);
  };

  const resetEditForm = () => {
    if (originalSelectedRecord) {
      setSelectedRecord({ ...originalSelectedRecord });
    }
    setNoticeError("");
  };

  const handleDateChange = (
    date: Dayjs | null,
    field: "notice" | "resignation",
    isEdit = false
  ) => {
    if (isEdit && selectedRecord) {
      const updatedRecord =
        field === "notice"
          ? { ...selectedRecord, Notice_Date: date?.format("YYYY-MM-DD") || "" }
          : {
              ...selectedRecord,
              Resignation_Date: date?.format("YYYY-MM-DD") || "",
            };

      setSelectedRecord(updatedRecord);
      validateNoticeAndResignation(
        dayjs(updatedRecord.Notice_Date),
        dayjs(updatedRecord.Resignation_Date)
      );
    } else {
      if (field === "notice") setAddNoticeDate(date);
      else setAddResignationDate(date);

      validateNoticeAndResignation(
        field === "notice" ? date : addNoticeDate,
        field === "resignation" ? date : addResignationDate
      );
    }
  };

  const validateNoticeAndResignation = (
    notice: Dayjs | null,
    resignation: Dayjs | null
  ) => {
    if (notice && resignation && !notice.isBefore(resignation)) {
      setNoticeError("Notice Date must be earlier than Resignation Date.");
    } else {
      setNoticeError("");
    }
  };

  // Handle opening Self Resignation modal
  const handleSelfResignation = () => {
    if (!canCreate) {
      return toast.warning("You don't have permission to create resignations.");
    }
    clearAddForm();
    setAddEmployeeId(currentUserId);
    setIsSelfResignation(true);
  };

  // Handle opening General Add Resignation modal
  const handleAddResignationModal = () => {
    if (!canCreate) {
      return toast.warning("You don't have permission to create resignations.");
    }
    clearAddForm();
    setIsSelfResignation(false);
  };

  const baseColumns: any[] = [
    {
      title: "Resigning Employee",
      dataIndex: "Resigning_Employee",
      render: (_: string, record: ResignationData) => (
        <div className="d-flex align-items-center">
          <Link to={all_routes.resignation} className="avatar avatar-md me-2">
            <img
              src={
                imageUrls[record.Resigning_Employee] ||
                "/assets/img/defaultavatar.png"
              }
              className="rounded-circle"
              alt="/assets/img/defaultavatar.png"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.onerror = null;
                target.src = "/assets/img/defaultavatar.png";
              }}
            />
          </Link>
          <h6 className="fw-medium">
            <Link to={all_routes.resignation}>{record.Username}</Link>
          </h6>
        </div>
      ),
    },
    { title: "Department", dataIndex: "Department" },
    { title: "Reason", dataIndex: "Reason" },
    {
      title: "Notice Date",
      dataIndex: "Notice_Date",
      render: (text: string) => (
        <span>
          {text && text !== "-" ? dayjs(text).format("DD-MM-YYYY") : "-"}
        </span>
      ),
    },
    {
      title: "Resignation Date",
      dataIndex: "Resignation_Date",
      render: (text: string) => (
        <span>
          {text && text !== "-" ? dayjs(text).format("DD-MM-YYYY") : "-"}
        </span>
      ),
    },
  ];

  const getColumns = () => {
    let columns = [...baseColumns];

    if (empRole !== "Admin" && empRole !== "Manager") {
      columns.push({
        title: "Status",
        key: "status",
        render: (record: ResignationData) => (
          <div className="d-flex align-items-center gap-2">
            <span
              className={`badge ${
                record.Status === "Approved"
                  ? "bg-success"
                  : record.Status === "Rejected"
                  ? "bg-danger"
                  : "bg-warning"
              }`}
            >
              {record.Status || "Pending"}
            </span>
            {record.Status === "Approved" && (
              <i className="ti ti-check text-success" title="Approved" />
            )}
            {record.Status === "Rejected" && (
              <i className="ti ti-x text-danger" title="Rejected" />
            )}
          </div>
        ),
      });

      columns.push({
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        render: (_: string, record: ResignationData) => (
          <div className="action-icon d-inline-flex">
            {record.Status === "Pending" && canUpdate && (
              <Link
                to="#"
                className="me-2"
                onClick={(e) => {
                  e.preventDefault();
                  if (!canUpdate) {
                    return toast.warning(
                      "You don't have permission to edit resignations."
                    );
                  }
                  setSelectedRecord(record);
                  setOriginalSelectedRecord({ ...record });
                }}
                data-bs-toggle={canUpdate ? "modal" : undefined}
                data-bs-target={canUpdate ? "#edit_resignation" : undefined}
              >
                <i className="ti ti-edit" />
              </Link>
            )}

            {record.Status === "Pending" && canDelete && (
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteClick(record.Resigning_Employee);
                }}
              >
                <i className="ti ti-trash" />
              </Link>
            )}

            {record.Status !== "Pending" && (
              <span className="text-muted small">No actions available</span>
            )}
          </div>
        ),
      });
    }

    if (empRole === "Admin" || empRole === "Manager") {
      // Status column
      columns.push({
        title: "Status",
        key: "resignation_status",
        render: (record: ResignationData) => {
          const isEditable = record.Status === "Pending";
          const isUpdating = updatingStatuses[record.Resigning_Employee];

          return (
            <div className="d-flex align-items-center gap-2">
              <Tag
                color={
                  record.Status === "Approved"
                    ? "green"
                    : record.Status === "Rejected"
                    ? "red"
                    : "gold"
                }
              >
                {record.Status || "Pending"}
              </Tag>

              {isEditable && (
                <Select
                  size="small"
                  style={{ width: 120 }}
                  placeholder="Update Status"
                  loading={isUpdating}
                  disabled={isUpdating}
                  onChange={(value: string) => {
                    if (value === "Approved") {
                      handleStatusUpdate(record.Resigning_Employee, "Approved");
                    } else if (value === "Rejected") {
                      handleRejectClick(record);
                    }
                  }}
                >
                  <Option value="Approved">Approved</Option>
                  <Option value="Rejected">Rejected</Option>
                </Select>
              )}
            </div>
          );
        },
      });

      // Actions column
      columns.push({
        title: "Actions",
        key: "actions",
        render: (_: any, record: ResignationData) => (
          <div className="action-icon d-inline-flex">
            {canDelete && (
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteClick(record.Resigning_Employee);
                }}
              >
                <i className="ti ti-trash" />
              </Link>
            )}
          </div>
        ),
      });
    }
    return columns;
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Resignation</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item active">Resignation</li>
                </ol>
              </nav>
            </div>
            {/* Button Section */}
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* General Add Resignation Button - Visible only to Admin/Manager */}
              {(empRole === "Admin" || empRole === "Manager") && canCreate && (
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center me-2"
                    onClick={handleAddResignationModal}
                    data-bs-toggle="modal"
                    data-bs-target="#new_resignation"
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add Resignation
                  </button>
                </div>
              )}
              {/* Self Resignation Button - Visible to all roles with permission */}
              {canCreate && (
                <div className="mb-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex align-items-center"
                    onClick={handleSelfResignation}
                    data-bs-toggle="modal"
                    data-bs-target="#new_resignation"
                  >
                    <i className="ti ti-user me-2" />
                    Self Resignation
                  </button>
                </div>
              )}
              <div className="ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* Table */}
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body p-0">
                  <Table
                    columns={getColumns()}
                    dataSource={resignationData}
                    rowKey={(record) => record.Resigning_Employee}
                    loading={loading}
                    Selection={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between bg-white border-top p-3">
          <p className="mb-0">2024 - 2025 © IATT.</p>
          <p>
            Designed &amp; Developed By{" "}
            <Link to="#" className="text-primary">
              IATT
            </Link>
          </p>
        </div>
      </div>
      {/* Add Resignation Modal - Single modal for both General and Self */}
      <div
        className="modal fade"
        id="new_resignation"
        tabIndex={-1}
        aria-labelledby="newResignationModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="newResignationModalLabel">
                {isSelfResignation ? "Self Resignation" : "Add Resignation"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={clearAddForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body pb-0">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Resigning Employee <span className="text-danger">*</span>
                  </label>
                  {isSelfResignation ? (
                    <input
                      type="text"
                      className="form-control"
                      value={currentUserDisplay}
                      disabled
                      readOnly
                    />
                  ) : (
                    <CommonSelect
                      className="select"
                      options={employeeOptions}
                      value={
                        employeeOptions.find(
                          (opt) => opt.value === addEmployeeId
                        ) || null
                      }
                      onChange={(option) =>
                        setAddEmployeeId(option ? option.value : "")
                      }
                    />
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Notice Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    value={addNoticeDate}
                    onChange={(date) => handleDateChange(date, "notice")}
                    className="form-control datetimepicker w-100"
                    format="DD-MM-YYYY"
                    getPopupContainer={getModalContainer}
                    placeholder="DD-MM-YYYY"
                  />
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Resignation Date <span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    value={addResignationDate}
                    onChange={(date) => handleDateChange(date, "resignation")}
                    className="form-control datetimepicker w-100"
                    format="DD-MM-YYYY"
                    getPopupContainer={getModalContainer}
                    placeholder="DD-MM-YYYY"
                  />
                  {noticeError && (
                    <div className="text-danger mt-1">{noticeError}</div>
                  )}
                </div>
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    Reason <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={addReason}
                    onChange={(e) => setAddReason(e.target.value)}
                    placeholder="Enter reason for resignation..."
                    maxLength={500}
                  />
                  <small className="text-muted">
                    {addReason.length}/500 characters
                  </small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-white border me-2"
                onClick={() => {
                  clearAddForm();
                }}
                data-bs-dismiss="modal"
              >
                Reset
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddResignation}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Resignation Modal */}
      <div
        className="modal fade"
        id="edit_resignation"
        tabIndex={-1}
        aria-labelledby="editResignationModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="editResignationModalLabel">
                Edit Resignation
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setSelectedRecord(null);
                  setOriginalSelectedRecord(null);
                  setNoticeError("");
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body pb-0">
              {selectedRecord && (
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Resigning Employee <span className="text-danger">*</span>
                    </label>
                    {empRole === "Employee" ? (
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value={
                            employeeOptions.find(
                              (opt) =>
                                opt.value === selectedRecord.Resigning_Employee
                            )?.label ||
                            currentUserFullName ||
                            "Current User"
                          }
                          disabled
                          readOnly
                          placeholder="Current User"
                        />
                        <span className="input-group-text">
                          <i className="ti ti-lock text-muted" />
                        </span>
                      </div>
                    ) : (
                      <CommonSelect
                        className="select"
                        options={employeeOptions}
                        value={
                          employeeOptions.find(
                            (opt) =>
                              opt.value === selectedRecord.Resigning_Employee
                          ) || null
                        }
                        onChange={(option) =>
                          setSelectedRecord({
                            ...selectedRecord,
                            Resigning_Employee: option ? option.value : "",
                          })
                        }
                      />
                    )}
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Notice Date <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                      value={
                        selectedRecord.Notice_Date
                          ? dayjs(selectedRecord.Notice_Date)
                          : null
                      }
                      onChange={(date) =>
                        handleDateChange(date, "notice", true)
                      }
                      className="form-control datetimepicker w-100"
                      format="DD-MM-YYYY"
                      getPopupContainer={getModalContainer}
                      placeholder="DD-MM-YYYY"
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Resignation Date <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                      value={
                        selectedRecord.Resignation_Date
                          ? dayjs(selectedRecord.Resignation_Date)
                          : null
                      }
                      onChange={(date) =>
                        handleDateChange(date, "resignation", true)
                      }
                      className="form-control datetimepicker w-100"
                      format="DD-MM-YYYY"
                      getPopupContainer={getModalContainer}
                      placeholder="DD-MM-YYYY"
                    />
                    {noticeError && (
                      <div className="text-danger mt-1">{noticeError}</div>
                    )}
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      value={selectedRecord.Reason}
                      onChange={(e) =>
                        setSelectedRecord({
                          ...selectedRecord,
                          Reason: e.target.value,
                        })
                      }
                      placeholder="Enter reason for resignation..."
                      maxLength={500}
                    />
                    <small className="text-muted">
                      {selectedRecord.Reason.length}/500 characters
                    </small>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-white border me-2"
                onClick={resetEditForm}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleUpdate}
                disabled={!canUpdate}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      <Modal
        title="Reject Resignation"
        open={rejectionModalVisible}
        onCancel={handleRejectCancel}
        confirmLoading={
          currentRejectEmployee
            ? updatingStatuses[currentRejectEmployee]
            : false
        }
        footer={[
          <button
            key="cancel"
            className="btn btn-white border me-2"
            onClick={handleRejectCancel}
            disabled={
              currentRejectEmployee
                ? updatingStatuses[currentRejectEmployee]
                : false
            }
          >
            Cancel
          </button>,
          <button
            key="submit"
            className="btn btn-danger"
            onClick={handleRejectSubmit}
            disabled={
              !rejectionReason.trim() ||
              (currentRejectEmployee
                ? updatingStatuses[currentRejectEmployee]
                : false)
            }
          >
            {currentRejectEmployee &&
            updatingStatuses[currentRejectEmployee] ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Submitting...
              </>
            ) : (
              "Reject"
            )}
          </button>,
        ]}
        width={500}
      >
        <div className="mb-3">
          <p>Are you sure you want to reject this resignation?</p>
          {currentRejectEmployee && (
            <p className="mb-0">
              <strong>Employee ID:</strong> {currentRejectEmployee}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="rejectionReason" className="form-label">
            Reason for Rejection <span className="text-danger">*</span>
          </label>
          <textarea
            id="rejectionReason"
            className="form-control"
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a detailed reason for rejecting this resignation..."
            disabled={
              currentRejectEmployee
                ? updatingStatuses[currentRejectEmployee]
                : false
            }
            maxLength={500}
          />
          <small className="text-muted">
            {rejectionReason.length}/500 characters
          </small>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Resignation"
        open={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        okButtonProps={{ danger: true }}
        confirmLoading={false}
      >
        <p>Are you sure you want to delete this resignation record?</p>
        {deleteEmployeeId && (
          <p className="mb-0">
            <strong>Employee ID:</strong> {deleteEmployeeId}
          </p>
        )}
        <p className="text-muted small">This action cannot be undone.</p>
      </Modal>
    </>
  );
};
export default Resignation;
